const STORAGE_KEY = 'tripening-theme'

type Theme = 'light' | 'dark' | 'system'

export function useTheme() {
  const resolvedTheme = useState<'light' | 'dark'>('resolved-theme', () => 'light')
  const userPreference = useState<Theme>('theme-preference', () => 'system')

  const applyTheme = (resolved: 'light' | 'dark') => {
    resolvedTheme.value = resolved
    if (import.meta.client) {
      document.documentElement.setAttribute('data-theme', resolved)
    }
  }

  const getSystemTheme = (): 'light' | 'dark' => {
    if (import.meta.client && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark'
    }
    return 'light'
  }

  const resolve = (pref: Theme): 'light' | 'dark' => {
    return pref === 'system' ? getSystemTheme() : pref
  }

  const setTheme = (pref: Theme) => {
    userPreference.value = pref
    applyTheme(resolve(pref))
    if (import.meta.client) {
      localStorage.setItem(STORAGE_KEY, pref)
    }
  }

  const toggle = () => {
    // Cycle: system → dark → light → system
    const next: Record<Theme, Theme> = {
      system: 'dark',
      dark: 'light',
      light: 'system',
    }
    setTheme(next[userPreference.value])
  }

  const init = () => {
    if (!import.meta.client) return

    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      userPreference.value = stored
    }
    applyTheme(resolve(userPreference.value))

    // Listen for OS theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (userPreference.value === 'system') {
        applyTheme(getSystemTheme())
      }
    })
  }

  return {
    theme: resolvedTheme,
    preference: userPreference,
    setTheme,
    toggle,
    init,
  }
}
